"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import type { ParsedText } from "@/lib/text-parser";

type Props = {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function TextReader({ scrollContainerRef }: Props) {
  const parsed = useStore((s) => s.parsed);
  const title = useStore((s) => s.title);
  const settings = useStore((s) => s.settings);
  const selectionMode = useStore((s) => s.selectionMode);
  const selectedWordId = useStore((s) => s.selectedWordId);
  const selectedSentenceId = useStore((s) => s.selectedSentenceId);
  const selectedParagraphId = useStore((s) => s.selectedParagraphId);
  const selectWord = useStore((s) => s.selectWord);

  // Track scroll progress
  const [progress, setProgress] = React.useState(0);
  const onScroll = React.useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max > 0 ? (el.scrollTop / max) * 100 : 0;
    setProgress(Math.min(100, Math.max(0, pct)));
  }, [scrollContainerRef]);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [onScroll, scrollContainerRef]);

  // Build a set of word ids that are "in current selection" for soft highlighting
  const softHighlightIds = React.useMemo(() => {
    const set = new Set<string>();
    if (!parsed) return set;
    if (selectionMode === "phrase" && selectedSentenceId) {
      const s = parsed.sentences.find((x) => x.id === selectedSentenceId);
      s?.wordIds.forEach((id) => set.add(id));
    }
    if (selectionMode === "paragraph" && selectedParagraphId) {
      const p = parsed.paragraphs.find((x) => x.id === selectedParagraphId);
      p?.wordIds.forEach((id) => set.add(id));
    }
    return set;
  }, [parsed, selectionMode, selectedSentenceId, selectedParagraphId]);

  // Auto-scroll selected word into view when selection changes via keyboard
  const lastClickedWordRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!selectedWordId) return;
    // Don't auto-scroll if this selection came from a click (click already positions it)
    if (lastClickedWordRef.current === selectedWordId) {
      lastClickedWordRef.current = null;
      return;
    }
    const el = document.getElementById(`word-${selectedWordId}`);
    if (!el) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    // If element is not in the middle 60% of the visible area, scroll it into view
    const visibleTop = cRect.top + cRect.height * 0.3;
    const visibleBottom = cRect.bottom - cRect.height * 0.3;
    if (elRect.top < visibleTop || elRect.bottom > visibleBottom) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedWordId, scrollContainerRef]);

  const handleWordClick = React.useCallback(
    (wordId: string, e: React.MouseEvent) => {
      e.preventDefault();
      lastClickedWordRef.current = wordId;
      selectWord(wordId);
    },
    [selectWord]
  );

  if (!parsed) return null;

  const fontFamilyClass =
    settings.fontFamily === "serif"
      ? "font-[var(--font-reader)]"
      : settings.fontFamily === "mono"
      ? "font-[var(--font-geist-mono)]"
      : "font-[var(--font-geist-sans)]";

  const horizontalPad = `${settings.marginWidth}%`;

  return (
    <div className="relative h-full flex flex-col reader-surface">
      {/* Scrollable text area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto reader-scroll reader-no-select"
        style={{ scrollBehavior: "auto" }}
      >
        <article
          className="mx-auto py-12"
          style={{
            paddingLeft: horizontalPad,
            paddingRight: horizontalPad,
            maxWidth: "78ch",
          }}
        >
          {/* Title */}
          <header className="mb-10 text-center">
            <h1
              className={cn(
                "font-semibold tracking-tight leading-tight",
                fontFamilyClass
              )}
              style={{
                fontSize: settings.fontSize * 1.6,
                color: "var(--reader-fg)",
                lineHeight: 1.2,
              }}
            >
              {title}
            </h1>
            <div
              className="mt-3 text-xs"
              style={{ color: "var(--reader-muted)" }}
            >
              {parsed.totalWords.toLocaleString()} words •{" "}
              {parsed.totalSentences.toLocaleString()} sentences •{" "}
              {parsed.totalParagraphs.toLocaleString()} paragraphs
            </div>
            <div
              className="mx-auto mt-4 h-px w-16"
              style={{
                background:
                  "linear-gradient(to right, transparent, var(--reader-muted), transparent)",
              }}
            />
          </header>

          {/* Render paragraphs */}
          {parsed.paragraphs.map((para) => (
            <ParagraphView
              key={para.id}
              para={para}
              parsed={parsed}
              settings={settings}
              fontFamilyClass={fontFamilyClass}
              selectedWordId={selectedWordId}
              softHighlightIds={softHighlightIds}
              selectionMode={selectionMode}
              onWordClick={handleWordClick}
            />
          ))}

          {/* Bottom padding for comfortable end-of-text scrolling */}
          <div className="h-32" />

          {/* End-of-text marker */}
          <div
            className="text-center text-xs pb-8"
            style={{ color: "var(--reader-muted)" }}
          >
            — Fim do texto —
          </div>
        </article>
      </div>

      {/* Progress bar at bottom */}
      <div className="shrink-0 h-1 bg-transparent relative">
        <div
          className="absolute inset-y-0 left-0 transition-[width] duration-150"
          style={{
            width: `${progress}%`,
            backgroundColor: "var(--reader-selection)",
            opacity: 0.55,
          }}
        />
      </div>
      <div
        className="shrink-0 px-4 py-1.5 text-[10px] flex items-center justify-between border-t"
        style={{
          color: "var(--reader-muted)",
          borderColor: "color-mix(in oklab, var(--reader-muted) 18%, transparent)",
          backgroundColor: "color-mix(in oklab, var(--reader-bg) 92%, var(--reader-fg) 8%)",
        }}
      >
        <span>{Math.round(progress)}% lido</span>
        <span className="tabular-nums">
          {(parsed.totalWords * (progress / 100)) | 0} /{" "}
          {parsed.totalWords.toLocaleString()} palavras
        </span>
      </div>
    </div>
  );
}

type ParaProps = {
  para: ParsedText["paragraphs"][number];
  parsed: ParsedText;
  settings: ReturnType<typeof useStore.getState>["settings"];
  fontFamilyClass: string;
  selectedWordId: string | null;
  softHighlightIds: Set<string>;
  selectionMode: "word" | "phrase" | "paragraph";
  onWordClick: (wordId: string, e: React.MouseEvent) => void;
};

function ParagraphView({
  para,
  parsed,
  settings,
  fontFamilyClass,
  selectedWordId,
  softHighlightIds,
  selectionMode,
  onWordClick,
}: ParaProps) {
  // For each word in the paragraph, render it with its trailing raw chars (whitespace, punctuation)
  const words = para.wordIds
    .map((id) => parsed.words.find((w) => w.id === id))
    .filter((w): w is NonNullable<typeof w> => Boolean(w));

  // Find each word's parent sentence so we can compute trailing chars from the sentence text.
  const sentencesById = React.useMemo(() => {
    const m = new Map<string, ParsedText["sentences"][number]>();
    for (const s of parsed.sentences) m.set(s.id, s);
    return m;
  }, [parsed]);

  const isParagraphSelected = selectionMode === "paragraph";

  return (
    <p
      className={cn("mb-6", fontFamilyClass)}
      style={{
        fontSize: `${settings.fontSize}px`,
        lineHeight: settings.lineHeight,
        letterSpacing: `${settings.letterSpacing}px`,
        textAlign: settings.justify ? "justify" : "left",
        hyphens: settings.hyphenate ? "auto" : "manual",
        textIndent: "1.5em",
        color: "var(--reader-fg)",
      }}
    >
      {isParagraphSelected && (
        <span
          aria-hidden
          className="select-none"
          style={{
            display: "inline-block",
            width: "0",
            marginLeft: "-1.5em",
          }}
        />
      )}
      {words.map((w, i) => {
        const isSelected = w.id === selectedWordId && selectionMode === "word";
        const isSoft = softHighlightIds.has(w.id);
        const sentence = sentencesById.get(w.sentenceId);
        const nextWord = words[i + 1];
        // If nextWord exists and is in the SAME sentence, trailing is the
        // gap between this word's end and nextWord's start (preserves
        // internal spaces and any mid-sentence punctuation).
        // If nextWord is in a DIFFERENT sentence (or there is no nextWord),
        // trailing is everything from this word's end to the end of the
        // current sentence, plus a single space if a next sentence follows
        // (so adjacent sentences are properly spaced).
        let trailing = "";
        if (sentence) {
          if (nextWord && nextWord.sentenceId === w.sentenceId) {
            trailing = sentence.text.slice(w.end, nextWord.start);
          } else {
            // Last word of this sentence: take remaining chars (e.g. final period).
            trailing = sentence.text.slice(w.end);
            // If there is a next sentence (different sentenceId), add a single
            // space between sentences for visual separation.
            if (nextWord && nextWord.sentenceId !== w.sentenceId) {
              trailing += " ";
            }
          }
        }
        return (
          <React.Fragment key={w.id}>
            <span
              id={`word-${w.id}`}
              className={cn(
                "reader-word",
                isSelected && "is-selected",
                selectionMode === "phrase" && isSoft && "in-sentence",
                selectionMode === "paragraph" && isParagraphSelected && "in-paragraph"
              )}
              onClick={(e) => onWordClick(w.id, e)}
              data-word-id={w.id}
              title={isSelected ? "" : "Clique para selecionar"}
            >
              {w.text}
            </span>
            {trailing}
          </React.Fragment>
        );
      })}
    </p>
  );
}
